import { Briefcase, Calendar, CheckSquare, Home, Phone, MessageSquare, Mail, Settings, Activity, type LucideIcon } from 'lucide-react'
import type { Notification, NotificationCategory } from '@/lib/notifications.api'
import { notificationDeepLink } from '@/lib/notification-link'
import { formatRelative } from '@/lib/utils'

const ICON_MAP: Record<NotificationCategory, LucideIcon> = {
  deals:      Briefcase,
  viewings:   Calendar,
  tasks:      CheckSquare,
  properties: Home,
  calls:      Phone,
  notes:      MessageSquare,
  emails:     Mail,
  system:     Settings,
  monitor:    Activity,
}

// Farba ikony podľa kategórie — 600 odtieň na svetlom, 400 na tmavom podklade
// (dropdown je bg-white / dark:bg-gray-800). Rovnaká paleta ako mobilný zoznam
// notifikácií (ICON_COLOR v app/notifications/index.tsx) — meniť spolu.
const COLOR_MAP: Record<NotificationCategory, string> = {
  deals:      'text-violet-600 dark:text-violet-400',
  viewings:   'text-sky-600 dark:text-sky-400',
  tasks:      'text-amber-600 dark:text-amber-400',
  properties: 'text-orange-600 dark:text-orange-400',
  calls:      'text-teal-600 dark:text-teal-400',
  notes:      'text-pink-600 dark:text-pink-400',
  emails:     'text-blue-600 dark:text-blue-400',
  system:     'text-slate-500 dark:text-slate-400',
  monitor:    'text-rose-600 dark:text-rose-400',
}
const FALLBACK_COLOR = 'text-slate-500 dark:text-slate-400'

export function NotificationItem({
  notification,
  onClick,
  compact = false,
}: {
  notification: Notification
  onClick: (link: string | null, id: string) => void
  compact?: boolean
}) {
  // Fall back to Settings for null OR any category not in the map (backend can emit
  // categories the frontend union doesn't know yet — an undefined component crashes React #130).
  const Icon: LucideIcon = (notification.category && ICON_MAP[notification.category]) || Settings
  const iconColor = (notification.category && COLOR_MAP[notification.category]) || FALLBACK_COLOR
  const isUnread = !notification.readAt
  const link = notificationDeepLink(notification)

  return (
    <button
      onClick={() => onClick(link, notification.id)}
      className={`w-full text-left flex gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
        isUnread ? '' : 'opacity-60'
      }`}
    >
      <div className={`flex-shrink-0 mt-0.5 ${iconColor}`}>
        <Icon size={compact ? 16 : 18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {notification.subject ?? 'Notifikácia'}
          </span>
          <span className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400">
            {formatRelative(notification.createdAt)}
          </span>
        </div>
        <div className="text-xs text-gray-600 dark:text-gray-300 truncate">{notification.body}</div>
      </div>
      {isUnread && (
        <div className="flex-shrink-0 w-2 h-2 mt-1.5 rounded-full bg-blue-500" />
      )}
    </button>
  )
}
